import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addPestsRoundingControl,
  FoodControlPointInstance,
  FoodManagementControls,
  InstanceStatus,
  parseRemarkAndCorrectiveActions,
  PestsRounding,
  updatePestsRoundingControl,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface AddPestsRoundingControlModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  type: FoodManagementControls.pestsRouding;
  initialData?: PestsRounding;
}

export function AddPestsRoundingControlModal({
  foodControlPointInstance,
  type,
  initialData,
  ...props
}: AddPestsRoundingControlModalProps) {
  const { t } = useTranslation();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState<boolean>(false);
  const [isRemarksNeeded, setIsRemarksNeeded] = useState<boolean>(false);
  const [note, setNote] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("no pests");
  const numberOfCorrectiveActionSelected = useRef(0);
  const addPestControlSchema = z.object({
    remarks: z
      .string()
      .optional()
      .refine((e) => !isRemarksNeeded, {
        message: t("food.Remarks are mandatory"),
      }),
    correctiveAction: z
      .boolean()
      .optional()
      .refine(
        (e) =>
          isCorrectiveActionNeeded
            ? !!numberOfCorrectiveActionSelected.current
            : true,
        {
          message: t("food.At leat one corrective action needed"),
        },
      ),
  });
  type AddPestControlData = z.infer<typeof addPestControlSchema>;

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<AddPestControlData>({
    resolver: zodResolver(addPestControlSchema),
  });

  const updateMutation = useMutation({
    mutationFn: updatePestsRoundingControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setNote([]);
      numberOfCorrectiveActionSelected.current = 0;
      setSelectedOption("no pests");
      props.onClose?.();
      toast.success(t("food.Pest Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.An error occurred while adding Pest Control"));
    },
  });

  const mutation = useMutation({
    mutationFn: addPestsRoundingControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setNote([]);
      numberOfCorrectiveActionSelected.current = 0;
      setSelectedOption("no pests");
      props.onClose?.();
      toast.success(t("food.Pest Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.An error occurred while adding Pest Control"));
    },
  });

  const noteTexts = {
    note1: t("food.Resolved Internally"),
    note2: t("food.Contact Pest"),
    note3: t("food.Food Safety Authority"),
    note4: t("food.Other"),
  };

  const editNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = noteTexts[e.target.name as keyof typeof noteTexts];
    const targetName = e.target.name;
    const isChecked = e.target.checked;
    isChecked
      ? ++numberOfCorrectiveActionSelected.current
      : --numberOfCorrectiveActionSelected.current;
    isChecked
      ? setNote((prev) => [...prev, noteText])
      : setNote((prev) => prev.filter((note) => note !== noteText));
    if (targetName === "note4" || targetName === "note1") {
      setIsRemarksNeeded(isChecked);
    }
  };

  const onSubmit = (data: AddPestControlData) => {
    const isCompeletedWithErrors = isCorrectiveActionNeeded;

    const payload = {
      companyId: foodControlPointInstance.companyId,
      companyLocationId: foodControlPointInstance.companyLocationId,
      reason: JSON.stringify(note),
      remark: data.remarks,
      controlPointInstanceId: foodControlPointInstance.id,
      value: selectedOption,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      foodControlPointInstance,
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }

    mutation.mutate(payload);
  };

  useEffect(() => {
    if (initialData) {
      const { reason, remark } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );

      setNote(reason);
      setIsCorrectiveActionNeeded(initialData.value !== "no pests");
      setSelectedOption(initialData.value);
      setIsRemarksNeeded(
        reason.includes(noteTexts.note4) || reason.includes(noteTexts.note1),
      );
      numberOfCorrectiveActionSelected.current = reason.length;
      reset({
        remarks: remark,
      });
    }
  }, [initialData, noteTexts.note1, noteTexts.note4, reset]);

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
      {...props}
      title={t("food.Pests Rounding")}
    >
      <form>
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-row items-center">
            <label className="form-label flex items-center gap-1  !font-normal">
              {t("food.No pests detected")}
            </label>
            <input
              name="pest"
              className={`radio radio-lg ${selectedOption === "no pests" && "!bg-[#296573] !border-[#296573]"}`}
              value="no pests"
              checked={selectedOption === "no pests"}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                setIsCorrectiveActionNeeded(false);
                setIsRemarksNeeded(false);
                setNote([]);
              }}
              type="radio"
            />
          </div>
          <div className="flex flex-row items-center">
            <label className="form-label flex items-center gap-1  !font-normal">
              {t("food.Signs of pests present")}
            </label>
            <input
              value="pests signs"
              name="pest"
              checked={selectedOption === "pests signs"}
              className={`radio radio-lg ${selectedOption === "pests signs" && "!bg-[#F6B100] !border-[#F6B100]"}`}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                setIsCorrectiveActionNeeded(true);
              }}
              type="radio"
            />
          </div>
          <div className="flex flex-row items-center">
            <label className="form-label flex items-center gap-1  !font-normal">
              {t("food.Rats detected")}
            </label>
            <input
              value="rats"
              name="pest"
              checked={selectedOption === "rats"}
              className={`radio radio-lg ${selectedOption === "rats" && "!bg-[#8D4F61] !border-[#8D4F61]"}`}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                setIsCorrectiveActionNeeded(true);
              }}
              type="radio"
            />
          </div>
          <div className="flex flex-row items-center">
            <label className="form-label flex items-center gap-1  !font-normal">
              {t("food.Pests detected no rats")}
            </label>
            <input
              value="pest no rats"
              name="pest"
              checked={selectedOption === "pest no rats"}
              className={`radio radio-lg ${selectedOption === "pest no rats" && "!bg-[#8D4F61] !border-[#8D4F61]"}`}
              onChange={(e) => {
                setSelectedOption(e.target.value);
                setIsCorrectiveActionNeeded(true);
              }}
              type="radio"
            />
          </div>
        </div>

        {isCorrectiveActionNeeded && (
          <>
            <div className="border-b border-b-gray-200 my-2.5"></div>
            <Label required title={t("food.Corrective Action")}>
              <div className="flex flex-col w-full gap-2 ">
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note1"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note1)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note1}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    onChange={(e) => editNote(e)}
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note2)}
                    name="note2"
                  />
                  {noteTexts.note2}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note3"
                    defaultChecked={note.includes(noteTexts.note3)}
                    className="checkbox checkbox-md"
                    type="checkbox"
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note3}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note4"
                    defaultChecked={note.includes(noteTexts.note4)}
                    className="checkbox checkbox-md"
                    type="checkbox"
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note4}
                </div>
                <ErrorMessage message={errors?.correctiveAction?.message} />
              </div>
            </Label>
          </>
        )}
        <div className="border-b border-b-gray-200 my-2.5"></div>
        <Label title={t("food.Remarks")}>
          <div className="flex flex-col items-end w-full gap-2 ">
            <Input
              {...register("remarks")}
              showRequired={isRemarksNeeded}
              placeholder="-"
              onChange={(e) => {
                setIsRemarksNeeded(!e.target.value);
              }}
            />
            <ErrorMessage message={errors?.remarks?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
